// src/components/notifications/NotificationProvider.jsx
import React from "react";
import { QueryClient, QueryClientProvider, useQueryClient } from "react-query";
import { NotificationQueryProvider } from "../../context/NotificationQueryContext";

// Create a query client instance for notifications (fallback)
const notificationQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // 1 minute
      cacheTime: 5 * 60 * 1000, // 5 minutes
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Inner component that checks for existing QueryClient
 */
const NotificationProviderInner = ({ children }) => {
  try {
    // Try to use existing QueryClient from context
    useQueryClient();

    // If we get here, there's already a QueryClient, just provide the notification context
    return <NotificationQueryProvider>{children}</NotificationQueryProvider>;
  } catch (error) {
    // No QueryClient in context, provide our own

    return (
      <QueryClientProvider client={notificationQueryClient}>
        <NotificationQueryProvider>{children}</NotificationQueryProvider>
      </QueryClientProvider>
    );
  }
};

/**
 * NotificationProvider Component
 *
 * Provides React Query and notification context to child components
 * Automatically detects if QueryClient already exists in context
 */
const NotificationProvider = ({ children, queryClient = null }) => {
  // If a specific queryClient is provided, use it
  if (queryClient) {
    return (
      <QueryClientProvider client={queryClient}>
        <NotificationQueryProvider>{children}</NotificationQueryProvider>
      </QueryClientProvider>
    );
  }

  // Otherwise, try to detect existing QueryClient
  return <NotificationProviderInner>{children}</NotificationProviderInner>;
};

export default NotificationProvider;
